import { ACCESS_TOKEN_KEY, PROFILE_BASE_PATH } from '@/constants';
import {
  LeaderboardResponse,
  LeaderboardResponseSchema,
  ProfilesListResponse,
  ProfilesListResponseSchema,
  MyProfileResponse,
  MyProfileResponseSchema,
  UpdateFplIdRequest,
  UpdateFplIdResponse,
  UpdateFplIdResponseSchema,
  ChangePasswordRequest,
  ChangePasswordResponse,
  ChangePasswordResponseSchema,
  UploadAvatarResponse,
  UploadAvatarResponseSchema,
  UpdateProfileSettingsRequest,
  UpdateProfileSettingsResponse,
  UpdateProfileSettingsResponseSchema,
  UpdateUiFlagsRequest,
  UpdateUiFlagsResponse,
  UpdateUiFlagsResponseSchema,
} from '@/types/profiles';
import { apiRequest, API_CONFIG, ApiHttpError } from './client';

const PROFILES_BASE = '/api/v1/profiles';

/** Thrown when API returns 301 global→duels redirect (caller may normalize URL). */
export class LeaderboardScopeRedirect extends Error {
  readonly targetScope = 'duels' as const;

  constructor() {
    super('Leaderboard scope redirected to duels');
    this.name = 'LeaderboardScopeRedirect';
  }
}

type LeaderboardApiScope = 'duels' | 'captain' | 'ai' | 'global' | 'country';

export interface GetProfilesParams {
  select?: '*';
  user_ids?: string; // comma-separated list
  username?: string; // exact match
  search?: string; // partial match (ILIKE)
  order?:
  | 'predictor_rating.desc'
  | 'predictor_rating.asc'
  | 'success_rate.desc'
  | 'success_rate.asc'
  | 'total_predictions.desc'
  | 'total_predictions.asc'
  | 'current_streak.desc'
  | 'current_streak.asc'
  | 'created_at.desc'
  | 'created_at.asc';
  limit?: number; // 1-100 default 50
  offset?: number; // default 0
  gameweek_id?: string; // filter leaderboard by gameweek (returns GW-specific stats)
  season_id?: string; // filter leaderboard by season aggregate (returns season-specific stats)
  scope?: LeaderboardApiScope;
  country?: string; // ISO 3166-1 alpha-2
}

function buildLeaderboardQuery(params: GetProfilesParams): URLSearchParams {
  const query = new URLSearchParams();
  if (params.select) query.set('select', params.select);
  if (params.user_ids) query.set('user_ids', params.user_ids);
  if (params.username) query.set('username', params.username);
  if (params.order) query.set('order', params.order);
  if (params.limit !== undefined) query.set('limit', String(params.limit));
  if (params.offset !== undefined) query.set('offset', String(params.offset));
  if (params.gameweek_id) query.set('gameweek_id', params.gameweek_id);
  if (params.season_id) query.set('season_id', params.season_id);
  if (params.search) query.set('search', params.search);
  if (params.scope) query.set('scope', params.scope);
  if (params.country) query.set('country', params.country);
  return query;
}

async function fetchLeaderboardRaw(params: GetProfilesParams): Promise<Response> {
  const query = buildLeaderboardQuery(params);
  const qs = query.toString();
  const url = `${API_CONFIG.BASE_URL}${PROFILES_BASE}/leaderboard${qs ? `?${qs}` : ''}`;

  const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  return fetch(url, { method: 'GET', headers, redirect: 'manual' });
}

export async function getProfiles(params: GetProfilesParams = {}): Promise<ProfilesListResponse> {
  const query = new URLSearchParams();
  if (params.select) query.set('select', params.select);
  if (params.user_ids) query.set('user_ids', params.user_ids);
  if (params.username) query.set('username', params.username);
  if (params.search) query.set('search', params.search);
  if (params.order) query.set('order', params.order);
  if (params.limit !== undefined) query.set('limit', String(params.limit));
  if (params.offset !== undefined) query.set('offset', String(params.offset));

  const qs = query.toString();
  const url = `${PROFILES_BASE}${qs ? `?${qs}` : ''}`;
  const res = await apiRequest<unknown>({ url, method: 'GET' });
  return ProfilesListResponseSchema.parse(res);
}

export async function getProfilesLeaderboard(params: GetProfilesParams = {}): Promise<LeaderboardResponse> {
  let response = await fetchLeaderboardRaw(params);

  if (response.status === 301) {
    response = await fetchLeaderboardRaw({ ...params, scope: 'duels' });
  }

  if (response.status === 410) {
    const body = (await response.json().catch(() => ({}))) as {
      message?: string;
      error?: string;
      redirect_league_id?: string | null;
    };
    throw new ApiHttpError(body.message ?? 'Country leaderboard moved to Leagues', {
      status: 410,
      code: body.error,
      details: { redirect_league_id: body.redirect_league_id ?? null },
    });
  }

  if (!response.ok) {
    const body = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new ApiHttpError(body.message ?? 'Request failed', { status: response.status });
  }

  const json = await response.json();
  return LeaderboardResponseSchema.parse(json);
}

// Get My Profile
export async function getMyProfile(): Promise<MyProfileResponse> {
  const res = await apiRequest<unknown>({ url: `${PROFILE_BASE_PATH}/me`, method: 'GET' });
  return MyProfileResponseSchema.parse(res);
}

// Upload Avatar
export async function uploadAvatar(file: File): Promise<UploadAvatarResponse> {
  const formData = new FormData();
  formData.append('file', file);

  // Use direct fetch for FormData uploads to avoid JSON content-type
  const accessToken = localStorage.getItem('auth_access_token');
  const headers: Record<string, string> = {};
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await fetch(`${API_CONFIG.BASE_URL}${PROFILE_BASE_PATH}`, {
    method: 'PATCH',
    headers,
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Upload failed' }));
    throw new Error(errorData.message || 'Upload failed');
  }

  const res = await response.json();
  return UploadAvatarResponseSchema.parse(res);
}

// Update FPL ID
export async function updateFplId(request: UpdateFplIdRequest): Promise<UpdateFplIdResponse> {
  const res = await apiRequest<unknown>({
    url: `${PROFILE_BASE_PATH}/fpl-id`,
    method: 'PATCH',
    data: request,
  });
  return UpdateFplIdResponseSchema.parse(res);
}

// Change Password
export async function changePassword(request: ChangePasswordRequest): Promise<ChangePasswordResponse> {
  const res = await apiRequest<unknown>({
    url: `${PROFILE_BASE_PATH}/password`,
    method: 'PUT',
    data: request,
  });
  return ChangePasswordResponseSchema.parse(res);
}

// Update Profile Settings
export async function updateProfileSettings(request: UpdateProfileSettingsRequest): Promise<UpdateProfileSettingsResponse> {
  const res = await apiRequest<unknown>({
    url: `${PROFILE_BASE_PATH}/settings`,
    method: 'PATCH',
    data: request,
  });
  return UpdateProfileSettingsResponseSchema.parse(res);
}

// Update UI flags (e.g. follow_x_modal_shown after displaying modal)
export async function updateProfileUiFlags(request: UpdateUiFlagsRequest): Promise<UpdateUiFlagsResponse> {
  const res = await apiRequest<unknown>({
    url: `${PROFILE_BASE_PATH}/ui-flags`,
    method: 'PATCH',
    data: request,
  });
  return UpdateUiFlagsResponseSchema.parse(res);
}

